/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiMeshOverviewComponent } from './components/api-mesh-overview/api-mesh-overview.component';
import { ApiMeshExplorerComponent } from './components/api-mesh-explorer/api-mesh-explorer.component';
import { ApiMeshSamplesComponent } from './components/api-mesh-samples/api-mesh-samples.component';
import { DynamicApiViewerComponent } from '../dynamic-api-viewer/dynamic-api-viewer.component';
import { DocumentationViewerComponent } from './components/documentation-viewer';
import { ApiMeshStateService, ApiMeshState } from './services/api-mesh-state.service';
import { DocumentationService } from './services/documentation.service';
import { ApiOperation } from './services/api-discoverability.service';
import {
  DocumentationConfig,
  DocumentationSection,
  DocumentationItem,
  DocumentationMenuState
} from './models/documentation.interface';

// Component for API mesh management with samples support
@Component({
  selector: 'app-api-mesh',
  standalone: true,
  imports: [
    CommonModule,
    ApiMeshOverviewComponent,
    ApiMeshExplorerComponent,
    ApiMeshSamplesComponent,
    DynamicApiViewerComponent,
    DocumentationViewerComponent
],
  templateUrl: './api-mesh.component.html',
  styleUrl: './api-mesh.component.scss'
})
export class ApiMeshComponent implements OnInit, AfterViewInit, OnDestroy {
  // Main section types - keeping existing sections plus dynamic docs
  activeSection: string = 'overview';

  // Static section flags
  isFeaturesExpanded: boolean = true;
  isDocumentsExpanded: boolean = false;
  isGettingStartedExpanded: boolean = false;
  isHowToUseExpanded: boolean = false;
  isSamplesExpanded: boolean = false;

  // Dynamic documentation state
  documentationConfig: DocumentationConfig | null = null;
  documentationMenuState: DocumentationMenuState | null = null;
  expandedDocSections: string[] = [];

  // API Explorer state
  showDynamicViewer: boolean = false;
  selectedOperation: ApiOperation | null = null;

  private stateSubscription?: Subscription;
  private documentationSubscription?: Subscription;
  private menuStateSubscription?: Subscription;

  constructor(
    private apiMeshStateService: ApiMeshStateService,
    private documentationService: DocumentationService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {
    // Subscribe to documentation menu state
    this.menuStateSubscription = this.documentationService.getMenuState().subscribe(state => {
      this.documentationMenuState = state;
    });

    // Store initial route info for later processing after documentation loads
    const routeSection = this.route.snapshot.data['section'] as string;
    const routeParam = this.route.snapshot.params['section'] as string;
    const fragment = this.route.snapshot.fragment;
    const queryParams = this.route.snapshot.queryParams;

    // Load documentation configuration and process initial route after it's loaded
    this.loadDocumentationConfig(() => {
      // Check for sample query parameter first (for direct sample viewing)
      if (queryParams['sample']) {
        const sampleType = queryParams['sample'];
        if (sampleType === 'typescript') {
          this.setActiveSection('sample-typescript');
        } else if (sampleType === 'javascript') {
          this.setActiveSection('sample-javascript');
        } else if (sampleType === 'angular') {
          this.setActiveSection('sample-angular');
        } else if (sampleType === 'backend-nodejs') {
          this.setActiveSection('sample-backend-nodejs');
        }
      } else if (routeSection) {
        this.setActiveSection(routeSection);
      } else if (routeParam) {
        this.setActiveSection(routeParam);
      } else if (fragment) {
        this.setActiveSection(fragment);
      }
    });

        // Subscribe to route parameter changes for dynamic documentation sections
    this.route.params.subscribe(params => {
      if (params['section']) {
        this.setActiveSection(params['section']);
      }
    });

    // Subscribe to query parameter changes for sample selection
    this.route.queryParams.subscribe(params => {
      if (params['sample']) {
        const sampleType = params['sample'];
        if (sampleType === 'typescript') {
          this.setActiveSection('sample-typescript');
        } else if (sampleType === 'javascript') {
          this.setActiveSection('sample-javascript');
        } else if (sampleType === 'angular') {
          this.setActiveSection('sample-angular');
        } else if (sampleType === 'backend-nodejs') {
          this.setActiveSection('sample-backend-nodejs');
        }
      }
    });

    // Subscribe to route fragment changes for documentation sections
    this.route.fragment.subscribe(fragment => {
      if (fragment) {
        // Wait for documentation config if not yet loaded
        if (this.documentationConfig) {
          this.handleFragmentChange(fragment);
        } else {
          // Defer fragment handling until config is loaded
          const configSub = this.documentationService.getDocumentationConfig().subscribe(config => {
            if (config) {
              this.handleFragmentChange(fragment);
              configSub.unsubscribe();
            }
          });
        }
      }
    });

    // Subscribe to API mesh state changes
    this.stateSubscription = this.apiMeshStateService.state$.subscribe((state: ApiMeshState) => {
      this.showDynamicViewer = state.showDynamicViewer;
      this.selectedOperation = state.selectedOperation;

      if (state.showDynamicViewer && routeSection == 'api-explorer') {
        // Ensure we're on the api-explorer section when dynamic viewer is shown
        this.activeSection = 'api-explorer';
      }
    });
  }

  ngAfterViewInit(): void {
    // Double-check fragment handling after view initialization
    // This ensures that if the initial fragment handling failed, we try again
    setTimeout(() => {
      const fragment = this.route.snapshot.fragment;
      if (fragment && this.documentationConfig) {
        this.processFragmentWithConfig(fragment);
      }
    }, 100);
  }

  setActiveSection(section: string): void {
    this.activeSection = section;

    // Auto-expand samples if selecting samples or a specific sample
    if (section === 'samples' || section === 'sample-typescript' || section === 'sample-javascript' || section === 'sample-angular' || section === 'sample-backend-nodejs') {
      this.isSamplesExpanded = true;
    }

    // Auto-expand documents if selecting a document section
    // Only check if documentation config is loaded, otherwise defer the check
    if (this.documentationConfig && this.isDocumentationSection(section)) {
      this.isDocumentsExpanded = true;
      // Also expand the specific documentation section
      this.expandDocumentationSection(section);
      // Navigate to the documentation section with the specific route
      this.navigateToDocumentationSection(section);
      return;
    } else if (!this.documentationConfig) {
      // If config is not loaded yet, wait for it and then check
      const configSub = this.documentationService.getDocumentationConfig().subscribe(config => {
        if (config && this.isDocumentationSection(section)) {
          this.isDocumentsExpanded = true;
          this.expandDocumentationSection(section);
          this.navigateToDocumentationSection(section);
        } else {
          // Handle other navigation logic for non-documentation sections only
          this.handleSectionNavigation(section);
        }
        configSub.unsubscribe();
      });
      return;
    }

    // Handle other navigation logic for non-documentation sections only
    this.handleSectionNavigation(section);
  }

  /**
   * Load documentation configuration
   */
  private loadDocumentationConfig(onLoaded?: () => void): void {
    this.documentationSubscription = this.documentationService.getDocumentationConfig().subscribe({
      next: (config) => {
        this.documentationConfig = config;
        // Execute callback after config is loaded
        if (onLoaded) {
          onLoaded();
        }
      },
      error: (error) => {
        console.error('Failed to load documentation config:', error);
      }
    });
  }

  /**
   * Handle fragment changes with proper documentation section handling
   */
  private handleFragmentChange(fragment: string): void {
    // Wait for documentation config to be loaded if needed
    if (!this.documentationConfig) {
      const configSub = this.documentationService.getDocumentationConfig().subscribe(config => {
        if (config) {
          this.processFragmentWithConfig(fragment);
          configSub.unsubscribe();
        }
      });
      return;
    }

    this.processFragmentWithConfig(fragment);
  }

  /**
   * Process fragment after documentation config is loaded
   */
  private processFragmentWithConfig(fragment: string): void {
    if (this.isDocumentationSection(fragment)) {
      // Don't call setActiveSection to avoid recursive navigation
      this.activeSection = fragment;
      this.isDocumentsExpanded = true;
      this.expandDocumentationSection(fragment);
    } else {
      // Handle non-documentation fragments
      this.setActiveSection(fragment);
    }
  }

  /**
   * Check if section is a documentation section
   */
  isDocumentationSection(section: string): boolean {
    if (!this.documentationConfig) return false;

    // Check if it's a top-level section
    if (this.documentationConfig.sections.some(s => s.route === section || s.id === section)) {
      return true;
    }

    // Check if it's a nested item
    for (const docSection of this.documentationConfig.sections) {
      if (this.findItemInSection(docSection, section)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Recursively find item in documentation section
   */
  private findItemInSection(section: DocumentationSection, targetId: string): boolean {
    if (section.route === targetId || section.id === targetId) {
      return true;
    }

    if (section.items) {
      for (const item of section.items) {
        if (this.findItemInItems(item, targetId)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Recursively find item in documentation items
   */
  private findItemInItems(item: DocumentationItem, targetId: string): boolean {
    if (item.route === targetId || item.id === targetId) {
      return true;
    }

    if (item.items) {
      for (const childItem of item.items) {
        if (this.findItemInItems(childItem, targetId)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Expand documentation section and its parents
   */
  private expandDocumentationSection(sectionId: string): void {
    if (!this.documentationConfig) return;

    // Find the path to this section and expand all parents
    for (const section of this.documentationConfig.sections) {
      const path = this.getPathToItem(section, sectionId, [section.id]);
      if (path) {
        // Expand all sections in the path
        path.forEach(id => {
          if (!this.expandedDocSections.includes(id)) {
            this.expandedDocSections.push(id);
          }
        });
        break;
      }
    }
  }

  /**
   * Get path to a documentation item
   */
  private getPathToItem(section: DocumentationSection | DocumentationItem, targetId: string, currentPath: string[]): string[] | null {
    const itemId = section.route || section.id;

    if (itemId === targetId) {
      return currentPath;
    }

    if ('items' in section && section.items) {
      for (const item of section.items) {
        const path = this.getPathToItem(item, targetId, [...currentPath, item.id]);
        if (path) {
          return path;
        }
      }
    }

    return null;
  }

  /**
   * Handle section-specific navigation logic
   */
  private handleSectionNavigation(section: string): void {
    // Auto-expand how to use if selecting a how-to-use sub-section
    if (section === 'nuget-package' || section === 'http-client' || section === 'http-protocol') {
      this.isHowToUseExpanded = true;
    }

    // Navigate to appropriate route (only on desktop)
    const sectionRoutes: Record<string, string | { path: string; queryParams: Record<string, string> }> = {
      'overview': '/api-mesh/overview',
      'documents': '/api-mesh',
      'getting-started': '/api-mesh',
      'how-to-access': '/api-mesh',
      'how-to-config': '/api-mesh',
      'how-to-use': '/api-mesh',
      'nuget-package': '/api-mesh',
      'http-client': '/api-mesh',
      'http-protocol': '/api-mesh',
      'discoverability': '/api-mesh',
      'best-practices': '/api-mesh',
      'api-explorer': '/api-mesh/explorer',
      'haystack': '/api-mesh/haystack',
      'features': '/api-mesh',
      'samples': '/api-mesh/samples',
      'sample-typescript': { path: '/api-mesh/samples', queryParams: { sample: 'typescript' } },
      'sample-javascript': { path: '/api-mesh/samples', queryParams: { sample: 'javascript' } },
      'sample-angular': { path: '/api-mesh/samples', queryParams: { sample: 'angular' } },
      'sample-backend-nodejs': { path: '/api-mesh/samples', queryParams: { sample: 'backend-nodejs' } },
      'hermes': '/api-mesh'
    };

    const route = sectionRoutes[section];
    if (route) {
      if (typeof route === 'string') {
        if (route !== this.router.url) {
          this.router.navigate([route]);
        }
      } else {
        this.router.navigate([route.path], { queryParams: route.queryParams });
      }
    }
  }

  /**
   * Navigate to documentation section with specific route
   */
  private navigateToDocumentationSection(section: string): void {
    // Just update the URL fragment without navigating to a different route
    // This preserves the current route but updates the fragment for bookmarking
    const currentUrl = this.router.url.split('#')[0]; // Remove existing fragment if any
    this.router.navigate([], {
      relativeTo: this.route,
      fragment: section,
      replaceUrl: true
    });
  }

  toggleFeaturesExpanded(): void {
    this.isFeaturesExpanded = !this.isFeaturesExpanded;
  }

  toggleSamplesExpanded(): void {
    this.isSamplesExpanded = !this.isSamplesExpanded;
  }

  handleSamplesMenuClick(): void {
    // If samples is already active, toggle expansion; otherwise just set it active and expand
    if (this.activeSection === 'samples') {
      this.toggleSamplesExpanded();
    } else {
      this.setActiveSection('samples');
    }
  }

  toggleDocumentsExpanded(): void {
    this.isDocumentsExpanded = !this.isDocumentsExpanded;
  }

  toggleDocumentsSection(): void {
    this.activeSection = 'documents';
    this.toggleDocumentsExpanded();
  }

  handleDocumentationSectionClick(section: any): void {
    const sectionIdentifier = section.route || section.id;

    // Toggle expansion FIRST if section has items (expand/collapse children)
    if (section.items) {
      this.toggleDocumentationSection(section.id);
    }

    // Then set active section if it has a route or id (display content)
    // Pass flag to prevent auto-expansion since we're handling it manually
    if (sectionIdentifier) {
      this.setActiveSectionManual(sectionIdentifier);
    }
  }

  handleDocumentationItemClick(item: any): void {
    const itemIdentifier = item.route || item.id;

    // Toggle expansion FIRST if item has children (expand/collapse children)
    if (item.items) {
      this.toggleDocumentationSection(item.id);
    }

    // Then set active section if it has a route or id (display content)
    // Pass flag to prevent auto-expansion since we're handling it manually
    if (itemIdentifier) {
      this.setActiveSectionManual(itemIdentifier);
    }
  }

  /**
   * Set active section without auto-expanding (used for manual clicks)
   */
  private setActiveSectionManual(section: string): void {
    this.activeSection = section;

    // Auto-expand samples if selecting samples or a specific sample
    if (section === 'samples' || section === 'sample-typescript' || section === 'sample-javascript' || section === 'sample-angular' || section === 'sample-backend-nodejs') {
      this.isSamplesExpanded = true;
    }

    // Auto-expand documents menu if selecting a document section
    if (this.documentationConfig && this.isDocumentationSection(section)) {
      this.isDocumentsExpanded = true;
      // Navigate to the documentation section with the specific route
      this.navigateToDocumentationSection(section);
      return;
    }

    // Handle other navigation logic for non-documentation sections
    this.handleSectionNavigation(section);
  }

  toggleGettingStartedExpanded(): void {
    this.isGettingStartedExpanded = !this.isGettingStartedExpanded;
  }

  toggleHowToUseExpanded(): void {
    this.isHowToUseExpanded = !this.isHowToUseExpanded;
  }

  toggleDocumentationSection(sectionId: string): void {
    const index = this.expandedDocSections.indexOf(sectionId);
    if (index > -1) {
      // Section is currently expanded, so collapse it (remove from array)
      this.expandedDocSections.splice(index, 1);
    } else {
      // Section is currently collapsed, so expand it (add to array)
      this.expandedDocSections.push(sectionId);
    }
  }

  isDocumentationSectionExpanded(sectionId: string): boolean {
    return this.expandedDocSections.includes(sectionId);
  }

  getDocumentTitle(): string {
    if (!this.documentationConfig) return 'API Mesh Documentation';

    // Find the title for the current active section in documentation config
    const title = this.findTitleForSection(this.activeSection);
    return title || 'API Mesh Documentation';
  }

  /**
   * Find title for a section in documentation configuration
   */
  private findTitleForSection(sectionId: string): string | null {
    if (!this.documentationConfig) return null;

    // Check top-level sections first
    for (const section of this.documentationConfig.sections) {
      if (section.route === sectionId || section.id === sectionId) {
        return section.title;
      }

      // Check nested items
      const nestedTitle = this.findTitleInItems(section.items || [], sectionId);
      if (nestedTitle) {
        return nestedTitle;
      }
    }

    return null;
  }

  /**
   * Recursively find title in documentation items
   */
  private findTitleInItems(items: DocumentationItem[], sectionId: string): string | null {
    for (const item of items) {
      if (item.route === sectionId || item.id === sectionId) {
        return item.title;
      }

      if (item.items) {
        const nestedTitle = this.findTitleInItems(item.items, sectionId);
        if (nestedTitle) {
          return nestedTitle;
        }
      }
    }

    return null;
  }

  getSectionTitle(): string {
    switch (this.activeSection) {
      case 'overview':
        return 'Overview';
      case 'documents':
        return 'Documentation';
      case 'getting-started':
        return 'Getting Started';
      case 'how-to-access':
        return 'How to get Access to Athena';
      case 'how-to-config':
        return 'How to get Configuration for Athena';
      case 'how-to-use':
        return 'How to use Athena';
      case 'nuget-package':
        return 'Via Athena NuGet Package';
      case 'http-client':
        return 'Via Standard HttpClient';
      case 'http-protocol':
        return 'Via Standard HTTP protocol';
      case 'discoverability':
        return 'Discoverability';
      case 'best-practices':
        return 'Best Practices & FAQ';
      case 'api-explorer':
        return 'API Explorer';
      case 'features':
        return 'Features';
      case 'samples':
        return "Samples";
      case 'sample-backend-nodejs':
        return 'API Mesh Integration<br/>Node.js + TypeScript Backend Sample';
      case 'sample-typescript':
        return 'API Mesh Integration<br/>TypeScript Frontend';
      case 'sample-javascript':
        return 'API Mesh Integration<br/>JavaScript Frontend';
      case 'sample-angular':
        return 'API Mesh Integration<br/>Angular Frontend';
      default:
        return 'API Mesh';
    }
  }

  getSectionSubtitle(): string {
    switch (this.activeSection) {
      case 'overview':
        return 'Brief overview of API Mesh capabilities';
      case 'documents':
        return 'Comprehensive documentation and guides';
      case 'getting-started':
        return 'Essential guides to begin using Athena';
      case 'how-to-access':
        return 'Learn how to obtain access to Athena APIs';
      case 'how-to-config':
        return 'Configure your application for Athena integration';
      case 'how-to-use':
        return 'General guidelines for using Athena APIs';
      case 'nuget-package':
        return 'Integration using the official .NET NuGet package';
      case 'http-client':
        return 'Direct integration using HTTP client libraries';
      case 'http-protocol':
        return 'Low-level HTTP protocol usage';
      case 'discoverability':
        return 'Explore and discover available API capabilities';
      case 'best-practices':
        return 'Best practices and frequently asked questions';
      case 'api-explorer':
        return 'Discover APIs available through the API Mesh';
      case 'features':
        return 'Discover the capabilities of API Mesh';
      case 'samples':
        return 'Investigate code samples';
      case 'haystack':
        return 'GCP points tag enrichment';
      case 'hermes':
        return 'Real-time messaging and event-driven architecture';
      default:
        return '';
    }
  }

  goBackToExplorer(): void {
    this.apiMeshStateService.closeDynamicViewer();
  }

  /**
   * Close the document panel and return to the documents list
   */
  closeDocument(): void {
    this.setActiveSection('documents');
  }

  ngOnDestroy(): void {
    if (this.stateSubscription) {
      this.stateSubscription.unsubscribe();
    }
    if (this.documentationSubscription) {
      this.documentationSubscription.unsubscribe();
    }
    if (this.menuStateSubscription) {
      this.menuStateSubscription.unsubscribe();
    }
  }
}

